import apiClient from '../apis';
import type { PromiseRow } from '../components/Agents/api';
import type { FeedCard } from '../components/Feed/api';

export interface QueuePart<T> { items: T[]; note: string | null }

/** A started review whose deck check has numbers to fix before the deck goes out (#569). */
export interface NumbersToFix {
  id: string; thread_id: string; title: string; deck: string | null; count: number; detail: string;
  counts: { stale: number; definition: number; needs: number };
}
/**
 * What a guardrail stopped. `run`: an agent whose last run a guardrail suppressed; the reason is as the viewer's
 * bar discloses it. `check` (#574): an action an agent checked that broke a guardrail — open until the objective's
 * owner decides it; `action` is the actor's words, null when the viewer's bar hides them.
 */
export interface BlockedAction {
  id: string; kind?: 'run' | 'check'; agent_id: string; agent_name: string | null; reason: string | null; at: string | null;
  objective?: string | null; actor?: { kind: 'agent' | 'person'; name: string | null } | null; action?: string | null; words_hidden?: boolean;
  /** the signed caller — who a person actor IS, and who checked on an agent's behalf */
  caller?: { id: string | null; name: string | null } | null;
}
/** One dated line of the week: the Activity timeline, review activity and the threads the viewer started. */
export interface WeekRow {
  id: string; kind: 'agent' | 'post' | 'promise' | 'knowledge' | 'review' | 'thread'; event: string; at: string; title: string;
  agent_id: string | null; thread_id?: string | null; tier?: FeedCard['tier'] | null;
  actor: { kind: 'agent' | 'person' | 'system'; name: string | null };
}
/** A lesson waiting on a person (#579): Knowledge › Needs review's rule; `detail` is where it came from. */
export interface Lesson { id: string; title: string; detail?: string | null; metric?: string | null }

export interface HomeQueueData {
  /** post_ids: every post that collapsed onto this row — repeat runs say the same sentence. Empty for a definition. */
  needs_your_say: QueuePart<{ id: string; kind: 'definition' | 'proposal' | 'contest'; title: string; agent_id?: string; post_ids: string[];
    /** a proposal that IS its objective's current plan (#572): how many actions, the expected move, the objective */
    plan?: { post_id: string; actions: number; objective: string | null; built_at: string | null;
      expected: { value: number; unit: string } | null } | null }>;
  promises: QueuePart<PromiseRow>;
  briefings: QueuePart<{ id: string; agent_id: string; title: string; published_at: string; summary: string }>;
  updates: QueuePart<FeedCard>;
  /** Redesign parts (#569); lessons (#579) are Knowledge › Needs review's, collapsed into one row on Home. */
  blocked_actions?: QueuePart<BlockedAction>;
  numbers_to_fix?: QueuePart<NumbersToFix>;
  lessons?: QueuePart<Lesson>;
  this_week?: QueuePart<WeekRow>;
  /** `review`: the redesign's last step — the viewer has a started review; null when it could not be read. */
  first_run: { steps: Array<{ key: string; done: boolean; hint: string }>; done: boolean; review?: boolean | null; agent_id: string | null; note: string | null };
}
export async function readHomeQueue(): Promise<HomeQueueData> {
  return (await apiClient.get<HomeQueueData>('/api/home/queue')).data;
}

/** One Trending row (#1505): a published metric's qualified id and its name, never a count. */
export interface TrendingMetric { id: string; name: string }

/**
 * Trending in your workspace this week (#1504): the server's ranked list, in its order. Anything but `{id, name}`
 * strings is dropped.
 */
export async function readTrending(): Promise<TrendingMetric[]> {
  const data = (await apiClient.get<{ metrics?: unknown }>('/api/home/trending')).data;
  const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
  return metrics.filter((m): m is TrendingMetric => !!m && typeof m === 'object'
    && typeof (m as any).id === 'string' && typeof (m as any).name === 'string' && !!(m as any).name.trim())
    .map((m) => ({ id: m.id, name: m.name }));
}
